import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import TagLayout from "@app/layouts/Tag/TagLayout";
import { SectionData } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import { extractUrlRedirect } from "@app/utils/extractUrlRedirect";
import {
  GA4_REF_CODE_ANCHORBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_L,
  GA4_REF_CODE_TITLE_STACKED,
} from "@app/utils/ga/ref-code";
import {
  mapTeaserDataToAnchor,
  mapTeaserDataToLatestProps,
} from "@app/utils/mapTeaserDataToProps";
import MetaData from "@components/Modules/MetaData";
import {
  mapErrorDataToMetaData,
  mapSectionDataToMetaData,
} from "@components/Modules/MetaData/mappers";
import PubBaseLayout from "@pub/BaseLayout";
import { configTag } from "@pub/config";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { LoaderFunctionArgs, useLoaderData, useLocation } from "react-router";

import { EventsTagsData, fetchEventsData } from "./Events.server";

type TagsData = SectionData & {
  queryLegacyTagData?: boolean;
};

export const meta = ({
  loaderData,
  location,
}: {
  loaderData: TRouteWithRedirect<TagsData, string>;
  location: { pathname: string };
}) => {
  if (loaderData.type !== ResponseType.SUCCESS) {
    const errorData = mapErrorDataToMetaData(loaderData, location);
    return MetaData(errorData);
  }

  const payload = loaderData.payload;
  const metaData = mapSectionDataToMetaData(payload);

  return MetaData(metaData);
};

export default function Events() {
  const response: TRouteWithRedirect<TagsData, string> = useLoaderData();
  const location = useLocation();

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }

  const data = response.payload;
  const topStory = (data.topStories.length && data.topStories[0]) || false;

  const metaData = mapSectionDataToMetaData(data);

  return (
    <PubBaseLayout>
      <AdSettings
        pageType="tag"
        sectionName={data.name}
        adUnits="lb1,lb2,imu1,imu2,catfish,prestitial"
        pageTitle={metaData.title}
        currentLocation={location}
      />

      <TagLayout
        data={{
          tagSlug: data.tagSlug,
          name: data.name,
          subHeading: configTag.subHeading,
          anchorBlock: topStory
            ? mapTeaserDataToAnchor(topStory, GA4_REF_CODE_ANCHORBLOCK_B)
            : undefined,
          latestArticle: data.topStories.length
            ? data.topStories
                .slice(1)
                .map((teaser) =>
                  mapTeaserDataToLatestProps(
                    teaser,
                    GA4_REF_CODE_TITLE_STACKED,
                  ),
                )
            : undefined,
          mainContentBlock: data.listedStories.map((teaser) =>
            mapTeaserDataToLatestProps(teaser, GA4_REF_CODE_CONTENTBLOCK_L),
          ),
          showVerticalLine: configTag.showVerticalLine,
          queryLegacyTagData: data.queryLegacyTagData,
          storyType: data.storyType,
        }}
      />
    </PubBaseLayout>
  );
}

export async function loader({
  request,
}: LoaderFunctionArgs): Promise<
  TRouteWithRedirect<EventsTagsData | GlobalBaseData, GlobalBaseData | string>
> {
  const data = await fetchEventsData();

  if (data.type === ResponseType.CLIENT_ERROR && data.statusCode === 404) {
    const redirectResult = await extractUrlRedirect(request.url);

    if (redirectResult && redirectResult.target) {
      return redirectResult;
    }
  }

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}
